import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  className?: string;
}

export default function Tooltip({ content, children, className = '' }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && isVisible) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          x: rect.left + rect.width / 2,
          y: rect.bottom + 8
        });
      }
    };

    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  const clonedChild = React.cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: (e: React.MouseEvent) => {
      handleMouseEnter();
      // Call original onMouseEnter if it exists
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e);
      }
    },
    onMouseLeave: (e: React.MouseEvent) => {
      handleMouseLeave();
      // Call original onMouseLeave if it exists
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e);
      }
    },
  });

  const tooltip = isVisible ? (
    <div
      className={`fixed w-64 p-3 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl transition-opacity duration-200 pointer-events-none ${className}`}
      style={{
        left: position.x - 128, // Center the tooltip (width/2)
        top: position.y,
        zIndex: 50,
      }}
    >
      <div className="text-xs text-gray-300">{content}</div>
      <div 
        className="absolute w-2 h-2 bg-gray-800 border-l border-t border-gray-600 transform rotate-45"
        style={{
          left: '50%',
          top: '-5px',
          marginLeft: '-4px'
        }}
      />
    </div>
  ) : null;

  return (
    <>
      {clonedChild}
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
}